// /src/pages/Dashboard.js
// Admin Dashboard: what an operator of an on-prem Aegis box needs at a glance.
//
// Replaces the previous version, which read /summary and /metrics/7day. Both
// were backed by an Open WebUI database that does not exist on this deployment,
// so the page rendered nothing but two error banners. Everything here comes
// from GET /api/admin/server_status.
//
// Each card renders its own section of the payload and its own error. One dead
// subsystem greys out one card instead of blanking the page.
import React, { useCallback, useEffect, useRef, useState } from "react";

const REFRESH_MS = 20000;

function api(path) {
  const token = localStorage.getItem("token");
  return fetch(`/api${path}`, {
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  }).then(async (res) => {
    if (!res.ok) {
      const raw = await res.text();
      let msg = raw;
      try { msg = JSON.parse(raw).error || raw; } catch { /* keep raw text */ }
      throw new Error(msg || `${res.status} ${res.statusText}`);
    }
    return res.json();
  });
}

// ---------------------------------------------------------------- formatting

function fmtBytes(n) {
  if (n === null || n === undefined) return "—";
  const units = ["B", "KB", "MB", "GB", "TB", "PB"];
  let v = Number(n);
  let i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
  return `${v.toFixed(v >= 100 || i === 0 ? 0 : 1)} ${units[i]}`;
}

function fmtUptime(seconds) {
  if (!seconds && seconds !== 0) return "—";
  const s = Math.floor(seconds);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function fmtNum(n) {
  if (n === null || n === undefined) return "—";
  return Number(n).toLocaleString();
}

// Tokens read better abbreviated once they get large.
function fmtTokens(n) {
  if (n === null || n === undefined) return "—";
  const v = Number(n);
  if (v >= 1e9) return `${(v / 1e9).toFixed(2)}B`;
  if (v >= 1e6) return `${(v / 1e6).toFixed(2)}M`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
  return String(v);
}

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
}

// ------------------------------------------------------------- small pieces

function SectionError({ message }) {
  return <div className="text-muted small fst-italic">Unavailable — {message}</div>;
}

function Card({ title, right, children }) {
  return (
    <div className="card shadow-sm h-100">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">{title}</h5>
          {right}
        </div>
        {children}
      </div>
    </div>
  );
}

// Usage bar. Colour is a threshold signal, not decoration: amber at 75%,
// red at 90%, so a filling disk is visible before it is a problem.
function UsageBar({ pct }) {
  if (pct === null || pct === undefined) return null;
  const variant = pct >= 90 ? "bg-danger" : pct >= 75 ? "bg-warning" : "bg-success";
  return (
    <div className="progress mt-1" style={{ height: 6 }}>
      <div
        className={`progress-bar ${variant}`}
        role="progressbar"
        style={{ width: `${Math.min(Math.max(pct, 0), 100)}%` }}
        aria-valuenow={pct}
        aria-valuemin="0"
        aria-valuemax="100"
      />
    </div>
  );
}

function Stat({ label, value, sub, pct }) {
  return (
    <div className="mb-3">
      <div className="text-muted small">{label}</div>
      <div className="fs-5 fw-bold">{value}</div>
      {sub && <div className="text-muted small">{sub}</div>}
      <UsageBar pct={pct} />
    </div>
  );
}

// Logical services are binary on purpose: a service is good only when every
// part of it is up, so "Aegis App" is bad if either half is down.
function StatusBadge({ row }) {
  return (
    <span className={`badge ${row.ok ? "text-bg-success" : "text-bg-danger"}`}>
      {row.ok ? "good" : "bad"}
    </span>
  );
}

// ------------------------------------------------------------------- cards

function ServerStatusCard({ host }) {
  const ram = host?.ram || {};
  const disk = host?.storage || {};

  return (
    <Card title="Server Status">
      <Stat
        label="Uptime"
        value={host?.uptime_error ? "—" : fmtUptime(host?.uptime_seconds)}
      />

      {ram.error ? <SectionError message={ram.error} /> : (
        <Stat
          label="Memory"
          value={`${fmtBytes(ram.free_bytes)} free`}
          sub={`${fmtBytes(ram.used_bytes)} used of ${fmtBytes(ram.total_bytes)}`}
          pct={ram.used_pct}
        />
      )}

      {disk.error ? <SectionError message={disk.error} /> : (
        <Stat
          label="Storage"
          value={`${fmtBytes(disk.free_bytes)} free`}
          sub={`${fmtBytes(disk.used_bytes)} used of ${fmtBytes(disk.total_bytes)}`}
          pct={disk.used_pct}
        />
      )}
    </Card>
  );
}

function ServicesCard({ services }) {
  const rows = services?.services || [];
  const down = services?.down || 0;

  const right = services?.error ? null : (
    <span className={`badge ${down ? "text-bg-danger" : "text-bg-success"}`}>
      {down ? `${down} degraded` : "all good"}
    </span>
  );

  return (
    <Card title="Aegis Services" right={right}>
      {services?.error ? <SectionError message={services.error} /> : (
        <div className="table-responsive">
          <table className="table table-sm align-middle mb-0">
            <thead>
              <tr>
                <th>Service</th>
                <th>Status</th>
                <th className="text-end">Restarts</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.name}>
                  <td>{r.name}</td>
                  <td>
                    <StatusBadge row={r} />
                    {/* When a multi-part service is degraded, say which ROLE
                        failed -- "backend down" -- never which container. */}
                    {!r.ok && r.detail && (
                      <div className="text-danger small">{r.detail}</div>
                    )}
                    {r.ok && r.uptime_seconds !== null && r.uptime_seconds !== undefined && (
                      <div className="text-muted small">up {fmtUptime(r.uptime_seconds)}</div>
                    )}
                  </td>
                  <td className="text-end">
                    <span className={r.restarts > 0 ? "text-warning" : "text-muted"}>
                      {r.restarts === null || r.restarts === undefined ? "—" : r.restarts}
                    </span>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan="3" className="text-muted">No services reported.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function EnginesCard({ engines }) {
  const rows = engines?.engines || [];
  return (
    <Card title="Model Engines">
      {rows.length === 0 ? <SectionError message="no engines configured" /> : rows.map((e) => (
        <div key={e.label} className="mb-3">
          <div className="d-flex justify-content-between align-items-center">
            <span className="fw-bold">{e.label}</span>
            <span className={`badge ${e.up ? "text-bg-success" : "text-bg-danger"}`}>
              {e.up ? "up" : "down"}
            </span>
          </div>
          {e.up ? (
            <>
              <div className="text-muted small">
                {e.requests_running} running · {e.requests_waiting} queued
                {e.kv_cache_pct !== null && e.kv_cache_pct !== undefined
                  && ` · KV cache ${e.kv_cache_pct}%`}
              </div>
              {/* These counters restart with the engine process, so they are
                  labelled as such rather than presented as lifetime totals. */}
              <div className="text-muted small">
                {fmtTokens(e.prompt_tokens)} in / {fmtTokens(e.generation_tokens)} out
                {" "}since engine start
              </div>
              <UsageBar pct={e.kv_cache_pct} />
            </>
          ) : (
            <SectionError message={e.error || "unreachable"} />
          )}
        </div>
      ))}
    </Card>
  );
}

function TokensCard({ tokens }) {
  const life = tokens?.lifetime || {};
  const win = tokens?.window_7d || {};

  return (
    <Card title="Token Throughput">
      {life.error ? <SectionError message={life.error} /> : (
        <div className="mb-3">
          <div className="text-muted small">Lifetime</div>
          <div className="d-flex justify-content-between align-items-end">
            <div>
              <div className="fs-5 fw-bold">{fmtTokens(life.tokens_in)}</div>
              <div className="text-muted small">Ingress</div>
            </div>
            <div className="text-end">
              <div className="fs-5 fw-bold">{fmtTokens(life.tokens_out)}</div>
              <div className="text-muted small">Egress</div>
            </div>
          </div>
          {/* "since" matters: the counters were installed mid-life, so this is
              not the box's whole history and must not be read as such. */}
          <div className="text-muted small mt-1">
            {fmtNum(life.interactions)} interactions since {fmtDate(life.since)}
          </div>
        </div>
      )}

      {win.error ? <SectionError message={win.error} /> : (
        <>
          <div className="text-muted small">Last 7 days</div>
          <div className="fw-bold mb-1">
            {fmtTokens(win.tokens_in)} in / {fmtTokens(win.tokens_out)} out
          </div>
          <table className="table table-sm mb-0">
            <tbody>
              {(win.by_surface || []).map((s) => (
                <tr key={s.surface}>
                  <td>{s.surface}</td>
                  <td className="text-end text-muted small">
                    {fmtTokens(s.tokens_in)} / {fmtTokens(s.tokens_out)}
                  </td>
                  <td className="text-end text-muted small">{fmtNum(s.interactions)}</td>
                </tr>
              ))}
              {(win.by_surface || []).length === 0 && (
                <tr><td className="text-muted small">No activity in the last 7 days.</td></tr>
              )}
            </tbody>
          </table>
        </>
      )}
    </Card>
  );
}

function DatabasesCard({ databases }) {
  const rows = databases?.databases || [];
  return (
    <Card title="Databases">
      <table className="table table-sm align-middle mb-3">
        <tbody>
          {rows.map((d) => (
            <tr key={d.label}>
              <td>
                <div>{d.label}</div>
                {!d.ok && <div className="text-muted small">{d.error}</div>}
              </td>
              <td className="text-end">
                <span className={`badge ${d.ok ? "text-bg-success" : "text-bg-danger"}`}>
                  {d.ok ? "ok" : "error"}
                </span>
              </td>
              <td className="text-end text-muted small">
                {d.ok ? fmtBytes(d.size_bytes) : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

// Sessions are stateless JWTs, so "signed in" is derived from the last sign-in
// falling inside the token lifetime rather than read from a session table.
// The window is always shown next to the number -- a bare "2 signed in" would
// imply a precision this cannot have.
function UsersCard({ users }) {
  if (users?.error) {
    return <Card title="Users"><SectionError message={users.error} /></Card>;
  }

  const sessionWindow = users?.session_minutes;
  const subCounts = [
    `${fmtNum(users?.admins)} admin`,
    users?.disabled ? `${fmtNum(users.disabled)} disabled` : null,
  ].filter(Boolean).join(" · ");

  return (
    <Card
      title="Users"
      right={
        <span className="text-muted small">
          {users?.last_login_at
            ? `Last sign-in ${fmtDate(users.last_login_at)}`
            : "No sign-ins recorded"}
        </span>
      }
    >
      <div className="row">
        <div className="col-6 col-lg-3">
          <Stat
            label="Signed in"
            value={fmtNum(users?.signed_in)}
            sub={sessionWindow ? `session valid ${sessionWindow} min` : "current sessions"}
          />
        </div>
        <div className="col-6 col-lg-2">
          <Stat label="Active 24h" value={fmtNum(users?.active_24h)} sub="signed in today" />
        </div>
        <div className="col-6 col-lg-2">
          <Stat label="Active 7d" value={fmtNum(users?.active_7d)} sub="signed in this week" />
        </div>
        <div className="col-6 col-lg-2">
          <Stat label="Active 30d" value={fmtNum(users?.active_30d)} sub="signed in this month" />
        </div>
        <div className="col-6 col-lg-3">
          <Stat label="Accounts" value={fmtNum(users?.total)} sub={subCounts} />
        </div>
      </div>

      {users?.never_logged_in > 0 && (
        <div className="text-muted small">
          {fmtNum(users.never_logged_in)} of {fmtNum(users?.total)} accounts have never signed in.
        </div>
      )}
    </Card>
  );
}

// -------------------------------------------------------------------- page

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  // Ref, not state: the poller reads it without being torn down and rebuilt on
  // every tick.
  const firstLoad = useRef(true);

  const load = useCallback(async () => {
    try {
      const payload = await api("/admin/server_status");
      setData(payload);
      setErr("");
    } catch (e) {
      setErr(e.message);
    } finally {
      if (firstLoad.current) {
        firstLoad.current = false;
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => clearInterval(t);
  }, [load]);

  return (
    <div className="container py-4">
      <div className="d-flex align-items-center mb-3">
        <h2 className="me-auto mb-0">Admin Dashboard</h2>
        <span className="text-muted small me-3">
          {data?.generated_at ? `Updated ${fmtDate(data.generated_at)}` : ""}
        </span>
        <button className="btn btn-sm btn-outline-secondary" onClick={load}>
          Refresh
        </button>
      </div>

      {/* Only a whole-endpoint failure gets a page-level banner. Section
          failures stay inside their own card. */}
      {err && <div className="alert alert-danger">{err}</div>}
      {loading && !data && <div className="alert alert-secondary py-2">Loading…</div>}

      {data && (
        <div className="row g-3">
          <div className="col-12 col-lg-4">
            <ServerStatusCard host={data.host} />
          </div>
          <div className="col-12 col-lg-4">
            <TokensCard tokens={data.tokens} />
          </div>
          <div className="col-12 col-lg-4">
            <EnginesCard engines={data.engines} />
          </div>
          <div className="col-12 col-lg-7">
            <ServicesCard services={data.services} />
          </div>
          <div className="col-12 col-lg-5">
            <DatabasesCard databases={data.databases} />
          </div>
          <div className="col-12">
            <UsersCard users={data.users} />
          </div>
        </div>
      )}
    </div>
  );
}
